/**
 * apiErrorMessage — zentrale Uebersetzung von API-Fehlern in i18n-Texte (R3-Review F2)
 *
 * Problem: `apiClient` wirft bei HTTP >= 400 eine `ApiError`, deren `message`
 * technisch ist ("API Error: 422"). Handler, die `e instanceof Error ? e.message : ...`
 * benutzen, zeigten diesen Rohtext direkt im UI an — unuebersetzt und ohne
 * Aussagekraft.
 *
 * Loesung: aus dem Response-Body `{ error: { code, message } }` den Code lesen und
 * ueber `common:apiErrors.codes.<code>` auf einen i18n-Text mappen. Fehlt der Code
 * (oder gibt es keinen Katalogeintrag), greift `common:apiErrors.status.<status>`
 * und zuletzt ein generischer Text bzw. der vom Aufrufer uebergebene Fallback.
 *
 * Explizit KEIN Durchreichen der BE-`message`: die ist serverseitig englisch und
 * nicht lokalisiert.
 */
import i18n from '../../i18n'
import { ApiError } from './ApiError'

const CODE_PREFIX = 'common:apiErrors.codes.'
const STATUS_PREFIX = 'common:apiErrors.status.'
const GENERIC_KEY = 'common:apiErrors.generic'

/** Technische ApiError-Message ("API Error: 422") — nie im UI zeigen. */
const TECHNICAL_MESSAGE = /^API Error: \d+$/

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}

/** Response-Body eines Fehlers (ApiError.body bzw. err.body / err selbst). */
export function bodyOf(err: unknown): Record<string, unknown> {
  if (err instanceof ApiError) return isRecord(err.body) ? err.body : {}
  if (isRecord(err) && isRecord(err.body)) return err.body
  if (isRecord(err)) return err
  return {}
}

/** HTTP-Status eines Fehlers, falls vorhanden. */
export function apiErrorStatus(err: unknown): number | undefined {
  if (err instanceof ApiError) return err.status
  if (isRecord(err) && typeof err.status === 'number') return err.status
  return undefined
}

/**
 * Fehlercode aus `{ error: { code } }` bzw. `{ code }` — normalisiert auf
 * lowercase, da das BE teils SCREAMING_SNAKE_CASE liefert (RATE_LIMIT_EXCEEDED).
 */
export function apiErrorCode(err: unknown): string | undefined {
  const body = bodyOf(err)
  const nested = isRecord(body.error) ? body.error.code : undefined
  const raw = typeof nested === 'string' ? nested : body.code
  if (typeof raw !== 'string' || raw.trim() === '') return undefined
  return raw.trim().toLowerCase()
}

function translateIfPresent(key: string): string | undefined {
  return i18n.exists(key) ? i18n.t(key) : undefined
}

/**
 * Liefert eine anzeigbare, uebersetzte Fehlermeldung.
 *
 * @param err      geworfener Fehler (ApiError, Error oder beliebiges Objekt)
 * @param fallback bereits uebersetzter, kontextspezifischer Fallbacktext
 */
export function apiErrorMessage(err: unknown, fallback?: string): string {
  const code = apiErrorCode(err)
  if (code) {
    const byCode = translateIfPresent(`${CODE_PREFIX}${code}`)
    if (byCode) return byCode
  }

  const status = apiErrorStatus(err)
  if (status !== undefined) {
    const byStatus = translateIfPresent(`${STATUS_PREFIX}${status}`)
    if (byStatus) return byStatus
  }

  if (fallback) return fallback

  // Nicht-API-Fehler (z.B. lokal geworfene, bereits uebersetzte Errors) duerfen
  // ihre Message behalten — nur die technische ApiError-Message nicht.
  if (err instanceof Error && err.message && !TECHNICAL_MESSAGE.test(err.message)) {
    return err.message
  }

  return i18n.t(GENERIC_KEY)
}
